import { useCallback, useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useSitzungVerlaengern, useWachBleiben } from '@/hooks/useStation';
import VertragAnzeige, { UV_STIL } from '@/components/unterschrift/VertragAnzeige';

/* Unterschrift-Station (Bauauftrag 03.10.2026) — die Seite fuer das iPad an der Theke.
   Vollbild ohne Menue und ohne Links in andere Bereiche. Zeigt dauerhaft die Warteseite und fragt
   alle zwei Sekunden, ob ein Vertrag zur Unterschrift gesendet wurde. Nach der Unterschrift fuenf
   Sekunden Bestaetigung, dann wieder Warteseite. Das Stationskonto darf serverseitig nur diese
   Seite (stationen.py) — die Oberflaeche ist nur die zweite Schicht. */

const ABFRAGE_MS = 2000;
const BESTAETIGUNG_MS = 5000;

// Wortlaut nach der Unterschrift je Vorgangsart (10.10.2026: auch Zahlerwechsel)
export const DANKE_TEXT = {
  vertrag: 'Dein Vertrag ist abgeschlossen. Du bekommst ihn gleich als PDF per E-Mail.',
  neue_laufzeit: 'Dein Vertrag mit neuer Laufzeit ist abgeschlossen. Du bekommst ihn gleich als PDF per E-Mail.',
  bestandsvertrag: 'Dein Vertrag ist digital bestätigt. Du bekommst ihn gleich als PDF per E-Mail.',
  zusatzvereinbarung: 'Die Zusatzvereinbarung gilt. Du bekommst sie gleich als PDF per E-Mail.',
  zahlerwechsel: 'Der Zahlerwechsel ist erfasst, das neue SEPA-Mandat gilt. Der Vertrag mit Nachtrag geht als PDF an die hinterlegte E-Mail.',
  leistungsangebot: 'Dein Angebot ist angenommen. Deine Termine und Bausteine erscheinen gleich in der App.',
  kontoaenderung: 'Danke! Deine neue Bankverbindung ist unterschrieben. Sobald das Studio sie bestätigt hat, bekommst du den Vertrag mit dem neuen SEPA-Mandat als PDF per E-Mail.',
};
export const dankeText = (art) => DANKE_TEXT[art] || DANKE_TEXT.vertrag;

export default function UnterschriftStationPage() {
  const { user, loading, logout, checkAuth } = useAuth();
  const [vorgang, setVorgang] = useState(null);
  const [zustand, setZustand] = useState('warten');     // warten | vertrag | danke | abgemeldet | kein_recht
  const [notiz, setNotiz] = useState('');
  const [abmelden, setAbmelden] = useState(false);
  const beschaeftigt = useRef(false);                    // waehrend der Uebertragung keine Abfrage-Ergebnisse uebernehmen
  const zustandRef = useRef(zustand); zustandRef.current = zustand;
  const vorgangRef = useRef(vorgang); vorgangRef.current = vorgang;
  const druck = useRef(null);

  useWachBleiben(true);
  useSitzungVerlaengern(!!user);

  const abfragen = useCallback(async () => {
    if (beschaeftigt.current || zustandRef.current === 'danke') return;
    try {
      const bekannt = vorgangRef.current?.id;
      const d = (await api.get('/unterschrift/station/aktuell', { params: bekannt ? { bekannt } : {} })).data;
      const neu = d.vorgang;
      if (beschaeftigt.current || zustandRef.current === 'danke') return;
      const alt = vorgangRef.current;
      if (neu && (!alt || alt.id !== neu.id)) { setVorgang(neu); setZustand('vertrag'); setNotiz(''); }
      else if (!neu && alt) {
        if (d.beendet === 'wird_abgeschlossen') return;          // Abschluss laeuft (per QR): naechste Abfrage entscheidet
        if (d.beendet === 'unterschrieben') {                    // am Handy unterschrieben: auch das iPad sagt Danke
          setZustand('danke');
          setTimeout(() => { setVorgang(null); setZustand('warten'); }, BESTAETIGUNG_MS);
        } else {
          setVorgang(null); setZustand('warten');
          setNotiz('Der Vorgang wurde beendet. Bitte an der Theke neu senden lassen.');
        }
      }
      else if (!neu && ['abgemeldet', 'kein_recht'].includes(zustandRef.current)) setZustand('warten');
    } catch (e) {
      const s = e?.response?.status;
      if (s === 401) { setVorgang(null); setZustand('abgemeldet'); }
      else if (s === 403) { setVorgang(null); setZustand('kein_recht'); }
      // Netz weg / Server im Neustart: Anzeige stehen lassen, naechste Abfrage versucht es wieder
    }
  }, []);

  useEffect(() => {
    if (loading || !user) return undefined;
    abfragen();
    const t = setInterval(abfragen, ABFRAGE_MS);
    return () => clearInterval(t);
  }, [loading, user, abfragen]);

  // Start ohne Nutzer, weil /auth/me beim Laden mit 502/503 oder Netzfehler scheiterte (Server im
  // Neustart): alle 5 s erneut pruefen, damit sich die Station von selbst erholt. Bei echtem 401
  // bleibt user leer und der Hinweis stehen.
  useEffect(() => {
    if (loading || user) return undefined;
    const t = setInterval(() => { checkAuth(); }, 5000);
    return () => clearInterval(t);
  }, [loading, user, checkAuth]);

  useEffect(() => { if (!notiz) return undefined; const t = setTimeout(() => setNotiz(''), 8000); return () => clearTimeout(t); }, [notiz]);
  // „Station abmelden" verschwindet nach zehn Sekunden wieder — kein Knopf, der fuer den naechsten Kunden stehen bleibt
  useEffect(() => { if (!abmelden) return undefined; const t = setTimeout(() => setAbmelden(false), 10000); return () => clearTimeout(t); }, [abmelden]);

  const absenden = async (eingabe) => {
    beschaeftigt.current = true;
    try {
      await api.post(`/unterschrift/station/${vorgang.id}/unterschreiben`, eingabe);
    } catch (e) {
      beschaeftigt.current = false;
      const s = e?.response?.status;
      // 404: der Vorgang ist zu Ende — vielleicht, weil ein erster Versuch ankam und nur die Antwort verloren ging.
      // Nicht raten: die naechste Abfrage sagt, wie er endete (unterschrieben -> „Danke", sonst der Hinweis).
      if (s === 404) { abfragen(); return false; }
      const d = e?.response?.data?.detail;
      throw Object.assign(new Error('abgelehnt'), { meldung: typeof d === 'string' ? d : null });
    }
    return true;
  };
  // Kontoänderung (Pia-Finanzen F2): das Mitglied tippt die IBAN am iPad; die Antwort ist die neue Anzeige (IBAN maskiert)
  const bankSenden = async (daten) => {
    try {
      const r = await api.post(`/unterschrift/station/${vorgang.id}/bank`, daten);
      setVorgang(r.data.vorgang);
    } catch (e) {
      const d = e?.response?.data?.detail;
      throw Object.assign(new Error('abgelehnt'), { meldung: typeof d === 'string' ? d : null });
    }
  };
  const fertig = () => {
    setZustand('danke');
    setTimeout(() => { beschaeftigt.current = false; setVorgang(null); setZustand('warten'); }, BESTAETIGUNG_MS);
  };

  // Abmelden gibt es nur nach langem Druck auf die Fusszeile — kein Link, den ein Kunde versehentlich trifft
  const druckStart = () => { if (zustandRef.current !== 'warten') return; druck.current = setTimeout(() => setAbmelden(true), 3000); };
  const druckEnde = () => clearTimeout(druck.current);

  const angemeldet = !loading && !!user;
  return (
    <div className="uv" data-testid="unterschrift-station">
      <style>{UV_STIL}</style>
      <header className="uv-kopf">
        <img src="/logo.png" alt="P1 Performance Center" />
        <span onPointerDown={druckStart} onPointerUp={druckEnde} onPointerLeave={druckEnde} data-testid="uv-fuss">Unterschrift · Theke</span>
      </header>

      {loading && <div className="uv-warten"><span className="uv-puls" /></div>}

      {!loading && (!user || zustand === 'abgemeldet') && (
        <div className="uv-warten" data-testid="uv-abgemeldet">
          <h1 className="uv-titel">Bitte neu anmelden</h1>
          <p>Die Anmeldung dieser Station ist abgelaufen. Bitte ein Teammitglied ansprechen.</p>
          <button type="button" className="uv-sekundaer" onClick={() => { window.location.href = '/login'; }}>Zur Anmeldung</button>
        </div>
      )}

      {angemeldet && zustand === 'kein_recht' && (
        <div className="uv-warten" data-testid="uv-kein-recht">
          <h1 className="uv-titel">Kein Stationsrecht</h1>
          <p>Dieses Konto darf die Unterschrift-Station nicht nutzen. Bitte mit dem Konto „Unterschrift-Station“ anmelden.</p>
          <button type="button" className="uv-sekundaer" onClick={async () => { await logout(); window.location.href = '/login'; }}>Abmelden</button>
        </div>
      )}

      {angemeldet && zustand === 'warten' && (
        <div className="uv-warten" data-testid="uv-warten">
          <img src="/logo.png" alt="" />
          <h1 className="uv-titel">Willkommen im P1</h1>
          <p>{notiz || 'Sobald dein Vertrag bereit ist, erscheint er hier zur Unterschrift.'}</p>
          <span className="uv-puls" aria-hidden="true" />
          {abmelden && <button type="button" className="uv-sekundaer" data-testid="uv-abmelden" onClick={async () => { await logout(); window.location.href = '/login'; }}>Station abmelden</button>}
        </div>
      )}

      {angemeldet && zustand === 'vertrag' && vorgang && (
        <VertragAnzeige key={vorgang.id} vorgang={vorgang} absenden={absenden} onFertig={fertig} bankSenden={bankSenden} />
      )}

      {angemeldet && zustand === 'danke' && (
        <div className="uv-warten" data-testid="uv-danke">
          <div className="uv-erfolg"><Check strokeWidth={3} /></div>
          <h1 className="uv-titel">Danke — unterschrieben</h1>
          <p>{dankeText(vorgang?.art)}</p>
        </div>
      )}
    </div>
  );
}
